// ReactCourse.js
import React, { useState } from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  Container,
  Divider,
  Button,
  SimpleGrid,
  List,
  ListItem,
  ListIcon,
  Input,
  FormControl,
  FormLabel,
  useToast,
} from "@chakra-ui/react";
import { CheckCircleIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";

const roadmap = [
  {
    level: "Basics",
    goals: "Understand how React works at a fundamental level; build simple apps",
    topics: [
      "1. Intro to React",
      "2. JSX & Rendering",
      "3. Components & Props",
      "4. State & Events",
      "5. Conditional & Lists",
      "6. useEffect & Data Fetching",
    ],
  },
  {
    level: "Intermediate",
    goals: "Build multi-page apps, state flows, form handling, API integration",
    topics: [
      "7. Context API",
      "8. useReducer & Custom Hooks",
      "9. React Router",
      "10. Forms & Controlled Components",
      "11. Side Effects & Data Fetching (advanced)",
    ],
  },
  {
    level: "Advanced",
    goals: "Build production-grade apps, optimize, test, use SSR / TypeScript",
    topics: [
      "12. Performance Optimization (memo, useMemo, useCallback)",
      "13. Code Splitting & Lazy Loading",
      "14. State Management (Redux / Zustand / Recoil)",
      "15. Testing (Jest, React Testing Library)",
      "16. Server-Side Rendering / Next.js",
      "17. TypeScript in React",
    ],
  },
  {
    level: "Expert / Deep Dives",
    goals: "Deep understanding, architecture decisions, future-oriented React",
    topics: [
      "18. React Internals (Fiber, reconciliation)",
      "19. Concurrent Mode / Suspense / Transitions",
      "20. Custom Renderers",
      "21. Advanced Patterns (Compound components, render props, HOCs)",
      "22. Micro-frontend, React in large apps",
    ],
  },
];

const ReactCourse = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [code, setCode] = useState("");

  // Validation function for 3 uppercase letters + 2 digits
  const validateCode = (input) => /^[A-Z]{3}\d{2}$/.test(input);

  const handleStartCourse = () => {
    if (validateCode(code)) {
      navigate("/learning-react");
    } else {
      toast({
        title: "Invalid Code",
        description: "Please get the correct code from the admin.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Container maxW="container.xl" py={10}>
      <VStack spacing={8} align="stretch">
        <Heading as="h1" size="2xl" textAlign="center">
          📘 React.js Learning Roadmap
        </Heading>
        <Text fontSize="lg" textAlign="center" color="gray.600">
          Here’s an overview of what you’ll cover — from <b>Basics</b> to{" "}
          <b>Expert</b>. Each chapter is short (&lt;30 min) and focused.
        </Text>

        <Divider />

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          {roadmap.map((section, idx) => (
            <Box
              key={idx}
              p={6}
              shadow="md"
              borderWidth="1px"
              borderRadius="lg"
              _hover={{ shadow: "lg", borderColor: "teal.400" }}
            >
              <Heading as="h3" size="lg" mb={3} color="teal.500">
                {section.level}
              </Heading>
              <Text mb={3} color="gray.600">
                🎯 {section.goals}
              </Text>
              <List spacing={2}>
                {section.topics.map((topic, i) => (
                  <ListItem key={i}>
                    <ListIcon as={CheckCircleIcon} color="teal.400" />
                    {topic}
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
        </SimpleGrid>

        <Divider />

        {/* Code Input Section */}
        <Box maxW="400px" mx="auto" textAlign="center">
          <FormControl mb={4}>
            <FormLabel>Enter Access Code</FormLabel>
            <Input
              placeholder="Get this from admin"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())} // force uppercase
              maxLength={5}
              textAlign="center"
              fontWeight="bold"
              fontSize="lg"
            />
          </FormControl>

          <Button
            size="lg"
            colorScheme="teal"
            onClick={handleStartCourse}
            px={10}
            py={6}
          >
            🚀 Start Course
          </Button>
        </Box>
      </VStack>
    </Container>
  );
};

export default ReactCourse;
